import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import { distances, border, colors } from '../../styles/constants';
import { H2, P } from '../../components/Typography';
import { Table, Tr, Th, Thead, Tbody, Td } from '../../components/Table';
import { Money } from '../../components/Formatters';
import type { Session, Transaction } from '../../types/checkout';
import { getVatAmount, getItems } from '../sessionHelpers';
import SplitPaymentRow from '../transactionDetails/components/SplitPaymentRow';
import Hr from '../../components/Hr';
export interface CustomerInfoProps extends WithTranslation {
    transaction?: Transaction;
    session?: Session;
}

const getCurrency = (session: Session | undefined): string => {
    if (session?.order.currency) {
        return session.order.currency;
    }
    return 'NOK';
};

const OrderItems = (
    {
        t,
        transaction,
        session,
    }: CustomerInfoProps
) => {
    const hasSessionOrder = session && session.order;
    const vatAmount = getVatAmount(session);

    // Using the items from the session since in some cases we are not guaranteed
    // to see all the items from the session on the transaction (eg. if the
    // session is a partial payment).
    const items = session && getItems(session);
    const discounts = session?.order?.discount_lines || [];


    const totalAmountOfItems = items?.reduce((sum, c) => (c?.amount || 0) + sum, 0);

    const currency = getCurrency(session);

    return (
        <Wrapper>
            <H2>{t('payments.transaction.order_items')}</H2>
            <Hr />
            {session && session.order.partial_payment && <P>{t('payments.transaction.order_is_partial_payment')}</P>}
            {!hasSessionOrder && <P>{t('payments.transaction.no_session_order')}</P>}
            {items && (
                <OrderItemsTable className="highlight inline">
                    <Thead>
                        <Tr>
                            <Th>{t('payments.fields.id')}</Th>
                            <Th>{t('payments.fields.description')}</Th>
                            <Th>{t('payments.fields.quantity')}</Th>
                            <Th className="vat collapse-650">{t('payments.fields.vat')} %</Th>
                            <Th className="vat collapse-650">{t('payments.fields.vat')}</Th>
                            <Th className="align-right amount">{t('payments.fields.amount')}</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {items.map((item, i) => (
                            <React.Fragment key={`${item.line_id}-----${i}`}>
                                <Tr className="no-wrap">
                                    <Td className="limit-100">{item.id}</Td>
                                    <Td>{item.description}</Td>
                                    <Td>{item.quantity}</Td>
                                    <Td className="vat collapse-650">{item.vat !== undefined && `${item.vat} %`}</Td>
                                    <Td className="vat collapse-650">
                                        {(item.vat_amount || item.vat_amount === 0) && (
                                            <Money amount={item.vat_amount} currency={currency} />
                                        )}
                                    </Td>
                                    <Td className="align-right amount">
                                        {(item.gross_amount || item.amount) && (
                                            <Money
                                                amount={item.gross_amount || item.amount || 0}
                                                currency={currency}
                                            />
                                        )}
                                    </Td>
                                </Tr>
                                {(item.discount_lines || []).map((discount) => (
                                    <Tr className="no-wrap">
                                        <Td>{discount.line_id}</Td>
                                        <Td>
                                            {[
                                                t('payments.fields.discount'),
                                                discount.discount_type &&
                                                    t([
                                                        `payments.fields.discount_types.${discount.discount_type}`,
                                                        discount.discount_type,
                                                    ]),
                                                discount.description,
                                            ]
                                                .filter((x) => x)
                                                .join(', ')}
                                        </Td>
                                        <Td></Td>
                                        <Td className="vat collapse-650"></Td>
                                        <Td className="vat collapse-650"></Td>
                                        <Td className="align-right amount">
                                            {discount.amount && (
                                                <Money amount={-discount.amount} currency={currency} />
                                            )}
                                        </Td>
                                    </Tr>
                                ))}
                                {item.splits && <Tr>
                                    <Td colSpan={6}>
                                        <SplitPaymentRow payoutSplits={item.splits} currency={currency} />
                                    </Td>
                                </Tr>}
                            </React.Fragment>
                        ))}
                        {discounts.map((discountItem, i) => (
                            <React.Fragment key={`discount-${discountItem.line_id}-----${i}`}>
                                <Tr className="no-wrap">
                                    <Td className="limit-100">{discountItem.discount_id}</Td>
                                    <Td>{
                                        [
                                            t('payments.fields.discount'),
                                            discountItem.discount_type &&
                                            t([
                                                `payments.fields.discount_types.${discountItem.discount_type}`,
                                                discountItem.discount_type,
                                            ]),
                                            discountItem.description,
                                        ]
                                            .filter((x) => x)
                                            .join(', ')
                                    }</Td>
                                    <Td></Td>
                                    <Td className="vat collapse-650"></Td>
                                    <Td className="vat collapse-650"></Td>
                                    <Td className="align-right amount">
                                        {(discountItem.amount) && (
                                            <Money
                                                amount={-discountItem.amount || 0}
                                                currency={currency}
                                            />
                                        )}
                                    </Td>
                                </Tr>
                            </React.Fragment>
                        ))}
                        <SummingRow>
                            <Td />
                            <Td />
                            <Td />
                            <Td className="collapse-650" />
                            <Td className="vat collapse-650">
                                {(vatAmount || vatAmount === 0) && (
                                    <Money amount={vatAmount} currency={currency} />
                                )}
                            </Td>
                            <Td className="align-right amount">
                                <Money amount={transaction?.amount ?? totalAmountOfItems ?? 0} currency={currency} />
                            </Td>
                        </SummingRow>
                    </Tbody>
                </OrderItemsTable>
            )}
        </Wrapper>
    );
};

export default withTranslation()(OrderItems);

const Wrapper = styled.div`
    padding-top: ${distances.normal};
`;



const SummingRow = styled.tr`
    border-top: ${border.normal} solid ${colors.borderLight};
    border-bottom: ${border.double} double ${colors.borderLight};
`;

const OrderItemsTable = styled(Table)`
    .amount,
    .vat {
        white-space: nowrap;
    }
`;
